import React, { useMemo, useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  Button,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"

import TableContainer from "../../../components/Common/DataTableContainer"
import ConfirmPopup from "components/ConfirmPopup"
import {
  getSystemUserList,
  addSystemUser,
  editSystemUser,
  removeSystemUser,
} from "../../../redux/actions/user"
import {
  ADD_SYSTEM_USER_RESET,
  EDIT_SYSTEM_USER_RESET,
  REMOVE_SYSTEM_USER_RESET,
} from "../../../redux/types/user"

const roleOptions = [
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
  { value: "volunteer", label: "Volunteer" },
]

const SystemUsers = () => {
  document.title = "System Users | PULA"

  const dispatch = useDispatch()

  const systemUserListScreen = useSelector(
    state => state.screens.getSystemUserList
  )
  const addSystemUserScreen = useSelector(state => state.screens.addSystemUser)
  const editSystemUserScreen = useSelector(
    state => state.screens.editSystemUser
  )
  const removeSystemUserScreen = useSelector(
    state => state.screens.removeSystemUser
  )

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [editRole, setEditRole] = useState("")
  const [removingUser, setRemovingUser] = useState(null)

  useEffect(() => {
    dispatch(getSystemUserList())
  }, [])

  useEffect(() => {
    if (addSystemUserScreen.success) {
      toast.success(addSystemUserScreen.message)
      setIsAddOpen(false)
      addFormik.resetForm()
      dispatch({ type: ADD_SYSTEM_USER_RESET })
      dispatch(getSystemUserList())
    }
    if (addSystemUserScreen.error) {
      toast.error(addSystemUserScreen.error)
      dispatch({ type: ADD_SYSTEM_USER_RESET })
    }
  }, [addSystemUserScreen])

  useEffect(() => {
    if (editSystemUserScreen.success) {
      toast.success(editSystemUserScreen.message)
      setEditingUser(null)
      dispatch({ type: EDIT_SYSTEM_USER_RESET })
      dispatch(getSystemUserList())
    }
    if (editSystemUserScreen.error) {
      toast.error(editSystemUserScreen.error)
      dispatch({ type: EDIT_SYSTEM_USER_RESET })
    }
  }, [editSystemUserScreen])

  useEffect(() => {
    if (removeSystemUserScreen.success) {
      toast.success(removeSystemUserScreen.message)
      setRemovingUser(null)
      dispatch({ type: REMOVE_SYSTEM_USER_RESET })
      dispatch(getSystemUserList())
    }
    if (removeSystemUserScreen.error) {
      toast.error(removeSystemUserScreen.error)
      setRemovingUser(null)
      dispatch({ type: REMOVE_SYSTEM_USER_RESET })
    }
  }, [removeSystemUserScreen])

  const addFormik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      mobile_no: "",
      email_id: "",
      user_type: "volunteer",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required("Name is required"),
      mobile_no: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number")
        .required("Mobile number is required"),
      email_id: Yup.string().email("Enter a valid email"),
      user_type: Yup.string()
        .oneOf(["admin", "staff", "volunteer"])
        .required("Role is required"),
    }),
    onSubmit: values => {
      dispatch(addSystemUser(values))
    },
  })

  const roleLabels = {
    admin: "Admin",
    staff: "Staff",
    volunteer: "Volunteer",
  }

  const tableData = useMemo(() => {
    return (systemUserListScreen.users || []).map(user => ({
      ...user,
      id: user.enc_user_id,
      role_label: roleLabels[user.user_type] || user.user_type,
    }))
  }, [systemUserListScreen.users])

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "full_name",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "Mobile",
        accessorKey: "mobile_no",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Email",
        accessorKey: "email_id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Role",
        accessorKey: "role_label",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Created Date",
        accessorKey: "created_date",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => {
          const row = info.row.original
          return (
            <div
              className="d-flex align-items-center gap-2"
              onClick={e => e.stopPropagation()}
            >
              <button
                className="btn btn-sm btn-info"
                onClick={() => {
                  setEditingUser(row)
                  setEditRole(row.user_type)
                }}
              >
                Edit Role
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => setRemovingUser(row)}
              >
                Remove
              </button>
            </div>
          )
        },
      },
    ],
    []
  )

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-end mt-3 mb-3">
          <div className="flex-grow-1">
            <h4 className="h4 mb-0 nom-title">System Users</h4>
          </div>
          <Button color="primary" onClick={() => setIsAddOpen(true)}>
            Add User
          </Button>
        </div>

        <TableContainer
          defaultPageSize={20}
          columns={columns}
          data={tableData}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder="Search records..."
          pagination="pagination"
          isAddButton={false}
          buttonClass="btn btn-primary"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          theadClass="table-light"
          divClassName="table-responsive mb-4 nominee-tbl-wrapper"
        />
      </div>

      {/* Add User Modal */}
      <Modal isOpen={isAddOpen} toggle={() => setIsAddOpen(!isAddOpen)} centered>
        <ModalHeader toggle={() => setIsAddOpen(!isAddOpen)}>
          Add System User
        </ModalHeader>
        <form onSubmit={addFormik.handleSubmit}>
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                name="first_name"
                value={addFormik.values.first_name}
                onChange={addFormik.handleChange}
                onBlur={addFormik.handleBlur}
                invalid={
                  addFormik.touched.first_name &&
                  !!addFormik.errors.first_name
                }
              />
              {addFormik.touched.first_name && addFormik.errors.first_name && (
                <div className="text-danger small mt-1">
                  {addFormik.errors.first_name}
                </div>
              )}
            </div>

            <div className="mb-3">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                name="last_name"
                value={addFormik.values.last_name}
                onChange={addFormik.handleChange}
              />
            </div>

            <div className="mb-3">
              <Label htmlFor="mobile_no">Mobile Number *</Label>
              <Input
                id="mobile_no"
                name="mobile_no"
                value={addFormik.values.mobile_no}
                onChange={addFormik.handleChange}
                onBlur={addFormik.handleBlur}
                invalid={
                  addFormik.touched.mobile_no && !!addFormik.errors.mobile_no
                }
              />
              {addFormik.touched.mobile_no && addFormik.errors.mobile_no && (
                <div className="text-danger small mt-1">
                  {addFormik.errors.mobile_no}
                </div>
              )}
            </div>

            <div className="mb-3">
              <Label htmlFor="email_id">Email (optional)</Label>
              <Input
                id="email_id"
                name="email_id"
                value={addFormik.values.email_id}
                onChange={addFormik.handleChange}
                onBlur={addFormik.handleBlur}
                invalid={
                  addFormik.touched.email_id && !!addFormik.errors.email_id
                }
              />
              {addFormik.touched.email_id && addFormik.errors.email_id && (
                <div className="text-danger small mt-1">
                  {addFormik.errors.email_id}
                </div>
              )}
            </div>

            <div className="mb-3">
              <Label htmlFor="user_type">Role *</Label>
              <Input
                id="user_type"
                name="user_type"
                type="select"
                value={addFormik.values.user_type}
                onChange={addFormik.handleChange}
              >
                {roleOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              color="secondary"
              onClick={() => setIsAddOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={addSystemUserScreen.loading}>
              {addSystemUserScreen.loading ? "Adding..." : "Add User"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Edit Role Modal */}
      <Modal isOpen={!!editingUser} toggle={() => setEditingUser(null)} centered>
        <ModalHeader toggle={() => setEditingUser(null)}>
          Edit Role — {editingUser?.full_name}
        </ModalHeader>
        <ModalBody>
          <Label htmlFor="edit_role">Role</Label>
          <Input
            id="edit_role"
            type="select"
            value={editRole}
            onChange={e => setEditRole(e.target.value)}
          >
            {roleOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditingUser(null)}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={editSystemUserScreen.loading}
            onClick={() =>
              dispatch(
                editSystemUser({
                  enc_user_id: editingUser.enc_user_id,
                  user_type: editRole,
                })
              )
            }
          >
            {editSystemUserScreen.loading ? "Saving..." : "Save"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remove Confirmation */}
      <ConfirmPopup
        title="Remove System User"
        isOpen={!!removingUser}
        message={`Remove ${removingUser?.full_name} from system users? They will lose ${removingUser?.role_label} access and become a regular user.`}
        onConfirm={() =>
          dispatch(removeSystemUser({ enc_user_id: removingUser.enc_user_id }))
        }
        confirmText="Remove"
        cancelText="Cancel"
        toggle={() => setRemovingUser(null)}
      />
    </div>
  )
}

export default SystemUsers
